import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import VangardHeader from "@/components/vangard/VangardHeader";
import VangardFooter from "@/components/vangard/VangardFooter";
import VMark from "@/components/vangard/VMark";
import Seo from "@/components/Seo";
const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const RELEASE_DATE = new Date("2026-10-30T09:00:00Z");

const CAT_MAP = {
  Interview: "vc.mag.cat-interviews",
  Business: "vc.mag.cat-business",
  "Luxury Lifestyle": "vc.mag.cat-lifestyle",
  Portfolio: "vc.mag.cat-portfolio",
  "The Table": "vc.mag.cat-table",
};
const CATEGORY_KEYS = ["vc.mag.cat-interviews", "vc.mag.cat-business", "vc.mag.cat-lifestyle", "vc.mag.cat-portfolio", "vc.mag.cat-table"];

const FEATURED = [
  {
    category: "Interview",
    title: "How he took a family shipping line and turned it into a private-jet fleet.",
    dek: "A twenty-year rebuild, told without the usual founder mythology.",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    minutes: 12,
  },
  {
    category: "Business",
    title: "Buying real estate the way a family office actually does it.",
    dek: "A Vangard member walks through her twelve-year, twelve-country portfolio.",
    image: "https://images.unsplash.com/photo-1554469384-e58fac16e23a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    minutes: 9,
  },
  {
    category: "Luxury Lifestyle",
    title: "The three-day itinerary a member keeps on hand for every guest.",
    dek: "Where he sends them, which restaurant closes for the table.",
    image: "https://images.unsplash.com/photo-1533105079780-92b9be482077?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    minutes: 6,
  },
];

const STORIES = [
  {
    category: "Portfolio",
    title: "Six members. Six companies. One decade of quiet compounding.",
    dek: "The businesses inside Vangard, told through the founders who built them.",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    minutes: 14,
  },
  {
    category: "The Table",
    title: "What a founder table sounds like at 2 a.m.",
    dek: "Notes from a closed dinner where three members audited each other's next moves.",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    minutes: 8,
  },
  {
    category: "Interview",
    title: "The COO who says her real job is protecting the founder's calendar.",
    dek: "On triage, refusal, and the discipline of not being helpful.",
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    minutes: 7,
  },
  {
    category: "Business",
    title: "A quiet exit: how a member sold a company he still runs.",
    dek: "The structure, the earn-out, and the answer he now gives to journalists.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    minutes: 11,
  },
];

function useCountdown(target) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1_000);
  return { days, hours, minutes, seconds };
}

/** A hand-set print-magazine cover mock-up rendered in pure CSS. */
function MagazineCover({ tilt = "-rotate-[3deg]", elevated = true, t }) {
  return (
    <div
      className={`relative w-full aspect-[3/4] rounded-[6px] overflow-hidden border border-white/[0.12] bg-neutral-950 ${tilt} ${
        elevated
          ? "shadow-[0_60px_120px_-30px_rgba(0,0,0,0.9),0_25px_60px_-15px_rgba(0,0,0,0.7)]"
          : "shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]"
      }`}
      data-testid="magazine-cover-mock"
    >
      <img
        src="https://images.unsplash.com/photo-1521790361543-f645cf042ec4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
        alt="Cover portrait for the VangardClub members magazine, first edition"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover grayscale contrast-[1.15]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/25 to-black/85 pointer-events-none" />
      <div className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none noise-overlay" />

      <div className="absolute top-0 left-0 right-0 px-5 pt-5">
        <div className="flex items-center justify-between text-white/80 text-[9px] tracking-[0.4em] uppercase">
          <span>N°01</span>
          <span>30 · 10 · 2026</span>
        </div>
        <div className="font-serif text-white leading-none tracking-tight mt-2" style={{ fontSize: "clamp(2.4rem, 6vw, 4.4rem)" }}>
          VANGARD
        </div>
        <div className="mt-1 h-px bg-white/30" />
        <div className="mt-1 flex items-center justify-between text-white/70 text-[9px] tracking-[0.35em] uppercase">
          <span>{t("vc.hero.eyebrow")}</span>
          <span>{t("vc.mag.hero-t1")} 01</span>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5 space-y-2">
        <div className="text-[9px] tracking-[0.35em] uppercase text-white/70">{t("vc.mag.cover-story")}</div>
        <div className="font-serif text-white leading-[1.05]" style={{ fontSize: "clamp(1rem, 2.4vw, 1.6rem)" }}>
          {t("vc.mag.cover-title-1")} <span className="italic text-neutral-300">{t("vc.mag.cover-title-italic")}</span>
        </div>
        <div className="pt-2 border-t border-white/20 flex items-center justify-between text-[9px] tracking-[0.3em] uppercase text-white/60">
          <span>{t("vc.mag.masthead-3")}</span>
          <span>Vangardclub.com</span>
        </div>
      </div>

      <div className="absolute top-1/2 -translate-y-1/2 right-4 rotate-[8deg]">
        <div className="w-16 h-16 rounded-full border border-white/60 flex flex-col items-center justify-center text-[8px] tracking-[0.3em] uppercase text-white/80">
          <span>{t("vc.mag.badge")}</span>
          <span className="mt-0.5 text-[7px]">{t("vc.mag.cover-title-italic")}</span>
        </div>
      </div>
    </div>
  );
}

function MemberBadge({ label }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-neutral-400 border border-white/15 rounded-full px-2.5 py-1"
      data-testid="member-only-badge"
    >
      <Lock size={10} />
      {label}
    </span>
  );
}

function ArticleCard({ article, testid, membersLabel, minLabel, t }) {
  return (
    <motion.article
      {...fade}
      className="group flex flex-col rounded-3xl overflow-hidden bg-[#0A0A0A] border border-white/[0.08] hover:border-white/25 transition-colors"
      data-testid={testid}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          loading="lazy"
          className="w-full h-full object-cover grayscale contrast-[1.05] transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-neutral-300 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
          {CAT_MAP[article.category] ? t(CAT_MAP[article.category]) : article.category}
        </div>
        <div className="absolute top-4 right-4">
          <MemberBadge label={membersLabel} />
        </div>
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-serif text-2xl leading-tight text-white">{article.title}</h3>
        <p className="mt-3 text-sm text-neutral-400 font-light leading-relaxed flex-1">{article.dek}</p>
        <div className="mt-6 flex items-center justify-between text-[11px] tracking-[0.2em] uppercase text-neutral-500">
          <span>{article.minutes} {minLabel}</span>
          <ArrowUpRight size={14} className="text-neutral-500 group-hover:text-white transition-colors" />
        </div>
      </div>
    </motion.article>
  );
}

export default function MagazinePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { days, hours, minutes, seconds } = useCountdown(RELEASE_DATE);
  const membersLabel = t("vc.mag.badge");
  const minLabel = t("vc.mag.min-read", { n: "" }).trim() || t("vc.mag.min");
  const articlesLocked = user && user.application_status !== "approved";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="magazine-page">
      <Seo title={t("vc.seo.magazine.title")} description={t("vc.seo.magazine.desc")} noindex />
      <VangardHeader />

      {/* ============================================================
          PRINT-MAGAZINE HERO — issue announcement + mock cover
          ============================================================ */}
      <section className="relative overflow-hidden pt-[112px] lg:pt-[68px]">
        <VMark position="bottom-left" size={780} opacity={0.05} />

        {/* Top masthead ticker */}
        <div className="relative border-b border-white/[0.06]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6 text-[10px] tracking-[0.4em] uppercase text-neutral-500">
            <div className="flex items-center gap-4">
              <span className="text-white">{t("vc.nav.magazine")}</span>
              <span className="h-px w-8 bg-white/20 hidden sm:inline-block" />
              <span className="hidden sm:inline">{t("vc.mag.masthead-1")}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="hidden md:inline">{t("vc.mag.masthead-2")}</span>
              <span className="h-px w-8 bg-white/20 hidden md:inline-block" />
              <span className="text-white">{t("vc.mag.masthead-3")}</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: massive issue announcement */}
          <div className="lg:col-span-7 relative">
            <motion.div {...fade}>
              <div className="flex items-baseline gap-4 text-neutral-400">
                <span className="text-[11px] tracking-[0.35em] uppercase">{t("vc.mag.hero-eyebrow-1")}</span>
                <span className="h-px w-10 bg-white/15" />
                <span className="text-[11px] tracking-[0.35em] uppercase">{t("vc.mag.hero-eyebrow-2")}</span>
              </div>

              {/* Editorial title with oversized N°01 */}
              <div className="mt-6 relative">
                <div
                  aria-hidden="true"
                  className="hidden sm:block font-serif text-white/[0.06] leading-none select-none pointer-events-none absolute -top-6 -left-2"
                  style={{ fontSize: "clamp(9rem, 22vw, 22rem)", letterSpacing: "-0.04em" }}
                >
                  N°01
                </div>
                <h1 className="relative font-serif text-[54px] sm:text-[72px] lg:text-[92px] leading-[0.95] tracking-tight">
                  {t("vc.mag.hero-t1")} <span className="italic text-neutral-300">{t("vc.mag.hero-italic")}</span>
                  <br />
                  {t("vc.mag.hero-t2")}
                  <br />
                  {t("vc.mag.hero-t3-1")} <span className="underline decoration-white/30 underline-offset-[10px]">{t("vc.mag.hero-t3-date")}</span>.
                </h1>
              </div>

              <p className="mt-10 text-base lg:text-lg text-neutral-400 font-light max-w-[48ch] leading-relaxed">
                {t("vc.mag.hero-body")}
              </p>

              {/* Live countdown */}
              <div className="mt-12">
                <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-4">
                  {t("vc.mag.releases-in")}
                </div>
                <div className="grid grid-cols-4 gap-3 max-w-md" data-testid="magazine-countdown">
                  {[
                    { l: t("vc.mag.days"), v: days },
                    { l: t("vc.mag.hours"), v: hours },
                    { l: t("vc.mag.minutes"), v: minutes },
                    { l: t("vc.mag.seconds"), v: seconds },
                  ].map((c) => (
                    <div
                      key={c.l}
                      className="rounded-2xl border border-white/[0.10] bg-[#0A0A0A] px-3 py-4 text-center"
                    >
                      <div className="font-serif text-3xl lg:text-4xl text-white tabular-nums">
                        {String(c.v).padStart(2, "0")}
                      </div>
                      <div className="text-[9px] tracking-[0.35em] uppercase text-neutral-500 mt-2">
                        {c.l}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  onClick={() => navigate(user ? "/account/profile" : "/register")}
                  className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
                  data-testid="magazine-cta-join"
                >
                  {user ? t("vc.mag.reserve-copy") : t("vc.mag.reserve-member")}
                  <ArrowUpRight size={16} />
                </button>
                <div className="text-[11px] tracking-[0.25em] uppercase text-neutral-500 max-w-[24ch]">
                  {t("vc.mag.printed-note")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: stacked mock covers */}
          <div className="lg:col-span-5 relative min-h-[520px] lg:min-h-[640px]">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -6 }}
              animate={{ opacity: 0.9, y: 0, rotate: -6 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-0 top-0 lg:top-6 lg:-left-4 w-[75%] max-w-[340px]"
            >
              <MagazineCover tilt="rotate-[-8deg]" elevated={false} t={t} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60, rotate: 4 }}
              animate={{ opacity: 1, y: 0, rotate: 4 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-0 top-14 lg:top-24 right-0 lg:right-2 w-[80%] max-w-[380px] ml-auto"
            >
              <MagazineCover tilt="rotate-[4deg]" elevated t={t} />
            </motion.div>

            {/* Wax-seal style stamp */}
            <div className="hidden lg:block absolute bottom-4 left-0 rotate-[-10deg]">
              <div className="w-28 h-28 rounded-full border-2 border-white/30 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-white">
                <span className="text-[9px] tracking-[0.35em] uppercase text-neutral-400">Vangard</span>
                <span className="font-serif text-3xl leading-none mt-1">N°01</span>
                <span className="text-[9px] tracking-[0.35em] uppercase text-neutral-400 mt-1">05·10·26</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom masthead line */}
        <div className="relative border-t border-white/[0.06]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-4 text-[10px] tracking-[0.4em] uppercase text-neutral-500">
            <div>{t("vc.mag.foot-1")}</div>
            <div className="text-white">{t("vc.mag.foot-2")}</div>
            <div>{t("vc.mag.foot-3")}</div>
          </div>
        </div>
      </section>

      {/* ================= Category rail ================= */}
      <section className="relative border-t border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-wrap gap-3">
          {CATEGORY_KEYS.map((c, i) => (
            <button
              key={c}
              className="text-[11px] tracking-[0.25em] uppercase text-neutral-400 border border-white/[0.10] rounded-full px-4 py-2 hover:text-white hover:border-white/45 transition-colors"
              data-testid={`magazine-category-${i}`}
            >
              {t(c)}
            </button>
          ))}
        </div>
      </section>

      {/* ================= Featured longreads ================= */}
      {articlesLocked ? (
        <section className="relative border-t border-white/[0.06] overflow-hidden">
          <VMark position="center" size={900} opacity={0.05} />
          <div className="relative max-w-[900px] mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
            <div className="inline-flex w-16 h-16 rounded-full border border-white/20 items-center justify-center mx-auto">
              <Lock size={22} />
            </div>
            <div className="mt-8 text-[11px] tracking-[0.35em] uppercase text-neutral-400">
              {t(`vc.onb.status.${user?.application_status || "none"}`)}
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-[1.05]">
              {t("vc.lock.title-1")} <span className="italic text-neutral-300">{t("vc.lock.italic")}</span>
            </h2>
            <p className="mt-6 text-neutral-400 font-light leading-relaxed max-w-[52ch] mx-auto">
              {t("vc.lock.body")}
            </p>
            <button
              onClick={() => navigate("/onboarding")}
              className="mt-10 inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
              data-testid="magazine-gated-cta"
            >
              {t("vc.lock.cta-onb")}
              <ArrowUpRight size={14} />
            </button>
          </div>
        </section>
      ) : (
      <>
      <section className="relative border-t border-white/[0.06] overflow-hidden bg-[#080808]">
        <VMark position="top-left" size={640} opacity={0.045} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.mag.inside")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">{t("vc.mag.longreads")}</h2>
            </div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-neutral-500">
              {t("vc.mag.every-subject")}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {FEATURED.map((a, i) => (
              <ArticleCard key={i} article={a} testid={`magazine-featured-${i}`} membersLabel={membersLabel} minLabel={minLabel} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* ================= Members-only manifesto ================= */}
      <section className="relative border-t border-white/[0.06] overflow-hidden">
        <VMark position="center" size={900} opacity={0.05} />
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
          <motion.div {...fade}>
            <MemberBadge label={membersLabel} />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.05]">
              {t("vc.mag.manifesto-title-1")} <span className="italic text-neutral-300">{t("vc.mag.manifesto-italic")}</span>
              <br /> {t("vc.mag.manifesto-title-2")}
            </h2>
            <p className="mt-8 text-base lg:text-lg text-neutral-400 font-light leading-relaxed max-w-[58ch] mx-auto">
              {t("vc.mag.manifesto-body")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ================= More stories ================= */}
      <section className="relative border-t border-white/[0.06] overflow-hidden bg-[#080808]">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.mag.more")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">{t("vc.mag.archive")}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STORIES.map((a, i) => (
              <ArticleCard key={i} article={a} testid={`magazine-story-${i}`} membersLabel={membersLabel} minLabel={minLabel} t={t} />
            ))}
          </div>
        </div>
      </section>
      </>
      )}

      {/* ================= CTA ================= */}
      <section className="relative border-t border-white/[0.06] overflow-hidden">
        <VMark position="bottom-right" size={720} opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <motion.div
            {...fade}
            className="rounded-3xl border border-white/[0.08] bg-[#0A0A0A] p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8"
          >
            <div className="max-w-[46ch]">
              <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.mag.cta-eyebrow")}</div>
              <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight">
                {t("vc.mag.cta-title-1")} <span className="italic text-neutral-300">{t("vc.mag.cta-italic")}</span>
              </h2>
              <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed">
                {t("vc.mag.cta-body")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate(user ? "/account/profile" : "/register")}
                className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
                data-testid="magazine-cta-join-2"
              >
                {user ? t("vc.mag.cta-open") : t("vc.mag.cta-join")}
                <ArrowUpRight size={14} />
              </button>
              <button
                onClick={() => navigate("/account/membership")}
                className="inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/55 transition-colors"
                data-testid="magazine-cta-plans"
              >
                {t("vc.mag.see-plans")}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <VangardFooter />
    </div>
  );
}
